<template>
  <div>
    <div class="mb-4 flex justify-end space-x-2 py-2">
      <MerchantGroupMerchantFilter v-model="schemaName" />
      <LinkToCreate
        :to="{ name: 'merchantGroupUserCreate' }"
      >
        Add User
      </LinkToCreate>
    </div>

    <BaseCard class="relative">
      <QueryLoading v-if="isLoading" />
      <QueryError
        v-else-if="isError"
        class="min-h-72"
        :show-retry="true"
        @retry="refetchMerchantGroupUsers"
      >
        Failed to retrieve merchant group users
      </QueryError>
      <template v-else>
        <div class="mb-2 flex w-full">
          <SearchInput v-model="rawSearch" />
        </div>
        <BaseTable
          v-model:ordering="ordering"
          :rows="data?.items ?? []"
          :columns="columns"
          :is-loading="isFetching"
          :get-row-class="getRowClass"
        >
          <template #cell-email="{ row, value }">
            <template v-if="!row.isActive">
              <span class="mr-2 text-error">
                INACTIVE
              </span>
            </template>

            {{ value }}
          </template>

          <template #cell-actions="{ row }">
            <BaseMenu>
              <template #button>
                <ButtonWithIcon>
                  <IconVerticalEllipsis
                    class="text-background-fg"
                  />
                </ButtonWithIcon>
              </template>

              <template #default>
                <BaseMenuItem
                  :to="{
                    name: 'merchantGroupUserEdit',
                    params: {
                      userId: row.id,
                    },
                  }"
                >
                  Edit User
                </BaseMenuItem>

                <BaseMenuItem
                  v-if="row.isActive"
                  @click="onSendUserLoginLink(row.id)"
                >
                  Send Portal Login Link
                </BaseMenuItem>

                <template v-if="row.isActive">
                  <BaseMenuItem
                    @click="onDeactivateUser(row.id)"
                  >
                    <span class="text-error">
                      Deactivate User
                    </span>
                  </BaseMenuItem>
                </template>

                <template v-else>
                  <BaseMenuItem
                    @click="onActivateUser(row.id)"
                  >
                    <span class="text-primary">
                      Activate User
                    </span>
                  </BaseMenuItem>
                </template>
              </template>
            </BaseMenu>
          </template>
        </BaseTable>

        <div class="flex justify-end py-2">
          <BasePagination
            v-model:page="page"
            :page-count="pageCount"
          />
        </div>
      </template>
    </BaseCard>
  </div>

  <BaseConfirmation
    ref="activateUserConfirmationRef"
    title="Activate User"
    description="This will allow the user to log in to the portal either via requesting a log in link or by using their password if it is set"
    confirm-label="Activate"
    is-positive
  />

  <BaseConfirmation
    ref="deactivateUserConfirmationRef"
    title="Deactivate User"
    confirm-label="Deactivate"
  >
    Deactivating a user will prevent them from being able to
    log into the web portal
    <br />
    <br />
    <div class="flex items-center text-sm">
      <IconExclamationCircle
        class="mr-2 size-8 text-info"
      />
      A user can be reactivated either on this page, or by
      requesting a log in link through the
      {{ organizationConfig?.appName }}
      application
    </div>
  </BaseConfirmation>
</template>

<script setup lang="ts">
import {
  type TableOrdering,
  type TableColumn,
} from "@/components/BaseTable.vue"
import type {
  ListMerchantGroupUsersFilter,
  ListMerchantGroupUsersOrdering,
  ListMerchantGroupUsersResponseItem,
} from "@/services/merchantGroup/users/listMerchantGroupUsers"

const rawSearch = ref("")

const debouncedSearch = debouncedRef(rawSearch, 300)

const filters = computed<ListMerchantGroupUsersFilter>(
  () => ({
    search: debouncedSearch.value.trim(),
  })
)

const {
  page,
  pageCount,
  paginationQuery,
  resetPagination,
} = usePagination({
  count: computed(() => data.value?.count || 0),
  limit: 10,
})

const ordering =
  ref<TableOrdering<ListMerchantGroupUsersOrdering>>()

const schemaName = ref<string | undefined>()

watch(
  () => filters,
  () => resetPagination(),
  { deep: true }
)

const query = reactiveComputed(() => ({
  ...filters.value,
  ordering: ordering.value,
  ...paginationQuery.value,
  schemaName: schemaName.value,
}))

const {
  data,
  isLoading,
  isFetching,
  isError,
  refetch: refetchMerchantGroupUsers,
} = useMerchantGroupUsers(query)

const { organizationConfig } =
  useMerchantGroupOrganizationConfig()

const now = useNow()

type ColumnId =
  | "merchantName"
  | "email"
  | "name"
  | "lastLogin"
  | "createdAt"
  | "actions"
const columns: Array<
  TableColumn<
    ListMerchantGroupUsersResponseItem,
    ColumnId,
    ListMerchantGroupUsersOrdering
  >
> = [
  {
    id: "merchantName",
    header: "Merchant",
    getCellValue: (row) => row.merchantName,
    autoWidth: true,
  },
  {
    id: "email",
    header: "Email",
    getCellValue: (row) => row.email,
    isSortable: true,
    autoWidth: true,
  },
  {
    id: "name",
    header: "Name",
    getCellValue: (row) =>
      `${row.firstName} ${row.lastName}`,
    isSortable: true,
  },
  {
    id: "lastLogin",
    header: "Last Login",
    getCellValue: (row) =>
      row.lastLogin
        ? DateFns.formatDistance(row.lastLogin, now.value, {
            addSuffix: true,
          })
        : "",
    isSortable: true,
    autoWidth: true,
  },
  {
    id: "createdAt",
    header: "Account Created",
    getCellValue: (row) =>
      row.createdAt
        ? DateFns.formatDistance(row.createdAt, now.value, {
            addSuffix: true,
          })
        : "",
    isSortable: true,
    autoWidth: true,
  },
  {
    id: "actions",
    header: "",
    align: "right",
    autoWidth: true,
  },
]

const activateUserConfirmationRef = useTemplateRef(
  "activateUserConfirmationRef"
)
const onActivateUser = async (
  userId: ListMerchantGroupUsersResponseItem["id"]
) => {
  if (activateUserConfirmationRef.value === null) return

  const { isCanceled } =
    await activateUserConfirmationRef.value.reveal()
  if (isCanceled) return
  activateMerchantGroupUser(userId)
}

const deactivateUserConfirmationRef = useTemplateRef(
  "deactivateUserConfirmationRef"
)
const onDeactivateUser = async (
  userId: ListMerchantGroupUsersResponseItem["id"]
) => {
  if (deactivateUserConfirmationRef.value === null) return

  const { isCanceled } =
    await deactivateUserConfirmationRef.value.reveal()
  if (isCanceled) return
  deactivateMerchantGroupUser(userId)
}

const onSendUserLoginLink = (
  userId: ListMerchantGroupUsersResponseItem["id"]
) => sendMerchantGroupUserLoginLink(userId)

const getRowClass = (
  row: ListMerchantGroupUsersResponseItem
) => {
  return !row.isActive ? "text-background-fg/30" : ""
}
</script>
